import { Injectable, computed, signal, untracked } from '@angular/core';
import { ChatPageContext } from '@core/services/ai/ai-assistant.service';

/** Hard cap on `visibleEntityIds` — a whole list view is not "focus". */
const MAX_VISIBLE_ENTITY_IDS = 20;

/** Serialized `ChatPageContext` byte cap before truncation kicks in. */
const MAX_PAGE_CONTEXT_BYTES = 2048;

/**
 * Opt-in channel for the current page to tell the AI assistant what it's
 * looking at — active tab, list filters, visible rows, open cockpit card
 * (issue #224). Pages call `setContext()` when they mount/change and
 * `clearContext()` on the way out; nothing here scrapes the DOM.
 *
 * `toolbar-ai-search.component.ts` merges this into `buildPageContext()`,
 * where a page hasn't opted in the route-regex fallback still supplies
 * `focusedEntityType`/`focusedEntityId`.
 */
@Injectable({ providedIn: 'root' })
export class AiPageContextProvider {
  private readonly _context = signal<ChatPageContext | undefined>(undefined);

  readonly context = computed(() => this._context());

  /**
   * Merges `partial` into the current context, capped and truncated.
   *
   * Callers are `effect()`s (cockpit detail, property/contract detail). The
   * previous value is read `untracked`: reading `_context()` directly made it a
   * dependency of the calling effect, which then wrote a fresh object to it and
   * was re-scheduled by its own write — an unbounded loop that froze the tab as
   * soon as a cockpit card was selected (dev, 2026-09-15). And a write that
   * changes nothing is skipped, so an effect re-running for an unrelated reason
   * does not fan out to every reader of `context()`.
   */
  setContext(partial: Partial<ChatPageContext>): void {
    this.write(this.sanitize({ ...untracked(this._context), ...partial }));
  }

  /** Replaces the current context wholesale (same guarantees as `setContext`). */
  replaceContext(context: ChatPageContext): void {
    this.write(this.sanitize(context));
  }

  /** Structural equality — the context is a small JSON-shaped object. */
  private write(next: ChatPageContext): void {
    const current = untracked(this._context);
    if (current && JSON.stringify(current) === JSON.stringify(next)) return;
    this._context.set(next);
  }

  /** Call on the way out (`ngOnDestroy`) so a closed page/card stops disambiguating turns. */
  clearContext(): void {
    this._context.set(undefined);
  }

  private sanitize(ctx: ChatPageContext): ChatPageContext {
    const capped: ChatPageContext = { ...ctx };
    if (capped.visibleEntityIds && capped.visibleEntityIds.length > MAX_VISIBLE_ENTITY_IDS) {
      capped.visibleEntityIds = capped.visibleEntityIds.slice(0, MAX_VISIBLE_ENTITY_IDS);
    }

    // Never let a caller's oversized filters/visibleEntityIds break the send —
    // drop the largest, least essential fields first until we fit the cap.
    while (
      JSON.stringify(capped).length > MAX_PAGE_CONTEXT_BYTES &&
      (capped.visibleEntityIds || capped.filters)
    ) {
      if (capped.visibleEntityIds) delete capped.visibleEntityIds;
      else delete capped.filters;
    }

    return capped;
  }
}
